import React, { useState, useEffect } from 'react';
import { Plus, Search, Users, UserCheck, UserX, Download, Upload, Edit, Trash2, Key } from 'lucide-react';
import DashboardLayout from '../DashboardLayout';
import { toast } from 'react-toastify';
import axios from 'axios';
import PersonalFormModal from './PersonalFormModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const PersonalManagement = () => {
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    es_lider: null,
    tiene_acceso_sistema: null,
    is_active: true,
    departamento: null
  });
  const [stats, setStats] = useState({
    total_lideres: 0,
    total_empleados: 0,
    total_activos: 0,
    total_con_acceso: 0
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  // Cargar datos
  useEffect(() => {
    fetchPersonal();
    fetchStats();
  }, [filters, searchTerm]);

  const fetchPersonal = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dashboardToken');
      const params = {
        ...filters,
        busqueda: searchTerm || undefined
      };

      const response = await axios.get(`${API_URL}/personal/`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setPersonal(response.data);
    } catch (error) {
      // console.error('Error al cargar personal:', error);
      toast.error('Error al cargar personal');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('dashboardToken');
      const response = await axios.get(`${API_URL}/personal/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data);
    } catch (error) {
      // console.error('Error al cargar estadísticas:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de desactivar este personal?')) return;

    try {
      const token = localStorage.getItem('dashboardToken');
      await axios.delete(`${API_URL}/personal/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Personal desactivado exitosamente');
      fetchPersonal();
      fetchStats();
    } catch (error) {
      // console.error('Error al desactivar personal:', error);
      toast.error(error.response?.data?.detail || 'Error al desactivar personal');
    }
  };

  const abrirAgregar = () => {
    setEditandoId(null);
    setModalOpen(true);
  };

  const abrirEditar = (id) => {
    setEditandoId(id);
    setModalOpen(true);
  };

  const handleSaved = () => {
    fetchPersonal();
    fetchStats();
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Personal</h1>
            <p className="text-gray-600 mt-1">Administra líderes y empleados de forma unificada</p>
          </div>
          <button
            onClick={abrirAgregar}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Persona
          </button>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Líderes</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total_lideres}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Empleados</p>
                <p className="text-2xl font-bold text-green-600">{stats.total_empleados}</p>
              </div>
              <Users className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Personal Activo</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_activos}</p>
              </div>
              <UserCheck className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Con Acceso Sistema</p>
                <p className="text-2xl font-bold text-orange-600">{stats.total_con_acceso}</p>
              </div>
              <Key className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nombre, documento, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={filters.es_lider === null ? '' : filters.es_lider}
                onChange={(e) => setFilters({ ...filters, es_lider: e.target.value === '' ? null : e.target.value === 'true' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="true">Líderes</option>
                <option value="false">Empleados</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Acceso Sistema</label>
              <select
                value={filters.tiene_acceso_sistema === null ? '' : filters.tiene_acceso_sistema}
                onChange={(e) => setFilters({ ...filters, tiene_acceso_sistema: e.target.value === '' ? null : e.target.value === 'true' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="true">Con acceso</option>
                <option value="false">Sin acceso</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={filters.is_active === null ? '' : filters.is_active}
                onChange={(e) => setFilters({ ...filters, is_active: e.target.value === '' ? null : e.target.value === 'true' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabla de personal */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Personal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cargo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Líder
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acceso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      Cargando...
                    </td>
                  </tr>
                ) : personal.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      No se encontró personal
                    </td>
                  </tr>
                ) : (
                  personal.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{p.nombre_completo}</div>
                            <div className="text-sm text-gray-500">{p.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {p.documento_identidad}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{p.cargo}</div>
                        {p.departamento && (
                          <div className="text-sm text-gray-500">{p.departamento}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          p.es_lider 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {p.es_lider ? 'Líder' : 'Empleado'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {p.nombre_lider || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {p.tiene_acceso_sistema ? (
                          <span className="flex items-center text-green-600">
                            <UserCheck className="w-4 h-4 mr-1" />
                            <span className="text-xs">{p.rol}</span>
                          </span>
                        ) : (
                          <span className="flex items-center text-gray-400">
                            <UserX className="w-4 h-4 mr-1" />
                            <span className="text-xs">Sin acceso</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          p.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {p.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => abrirEditar(p.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="text-red-600 hover:text-red-900 ml-3"
                          title="Desactivar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <PersonalFormModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          personalId={editandoId}
          onSaved={handleSaved}
        />
      </div>
    </DashboardLayout>
  );
};

export default PersonalManagement;
